import {
  U3141EmptyChildLotChain,
  type U3141ChildLotChain,
  type U3141ResolveLotDetailForGraphAddResult,
} from '../U3141Service';

function formatCorrectionsForForm(corrections: string[] | null | undefined): string {
  if (!corrections?.length) return '';
  return corrections.filter((c) => typeof c === 'string' && c.trim() !== '').join(', ');
}

/**
 * Mappe la réponse resolveLotDetailForGraphAdd vers un brouillon formulaire (sans child id).
 */
export function u3141MapResolveGraphAddResultToChild(
  resolve: U3141ResolveLotDetailForGraphAddResult,
  lotFallback: string,
): U3141ChildLotChain {
  const preview = resolve.lotDetail;
  if (!preview) {
    return {
      ...U3141EmptyChildLotChain,
      lotDetail: {
        ...U3141EmptyChildLotChain.lotDetail,
        lot: lotFallback.trim(),
        lotState: 'notValidate',
        concordance: [],
        concordanceOrigines: [],
      },
    };
  }

  const lotState = preview.lotState === 'validate' ? 'validate' : 'notValidate';
  const concordance = Array.isArray(preview.concordance) ? [...preview.concordance] : [];

  return {
    ...U3141EmptyChildLotChain,
    lotDetail: {
      ...U3141EmptyChildLotChain.lotDetail,
      id: preview.id,
      lot: preview.lot?.trim() || lotFallback.trim(),
      partieDeLot: preview.partieDeLot === true,
      district: {
        ...U3141EmptyChildLotChain.lotDetail.district,
        propertyDistrict: (preview.propertyDistrict ?? '').trim(),
      },
      cadastre: {
        ...U3141EmptyChildLotChain.lotDetail.cadastre,
        cadastre: (preview.cadastre ?? '').trim(),
        rank: (preview.rank ?? '').trim(),
        rankLabel: (preview.rank ?? '').trim(),
      },
      dateEstablishment: preview.dateEstablishment ?? '',
      lotState,
      operationType: preview.operationType ?? '',
      operationTypeInfo: preview.operationTypeInfo ?? null,
      operationTypeInfoEnabled: preview.operationTypeInfoEnabled,
      corrections: formatCorrectionsForForm(preview.corrections),
      concordance,
      concordancePartieDeLot: preview.concordancePartieDeLot ?? concordance.map(() => false),
      concordanceOrigines: [],
    },
  };
}

/** Réponse « lot absent du registre » (saisie manuelle BL-0661). */
export function u3141ResolveGraphAddNotFoundResult(): U3141ResolveLotDetailForGraphAddResult {
  return { found: false, alreadyInMasterChain: false };
}

/** Extrait `result` depuis l'enveloppe API U2000 ou un corps plat. */
export function u3141ParseResolveLotDetailForGraphAddResponse(
  data: unknown,
): U3141ResolveLotDetailForGraphAddResult | null {
  if (data == null || typeof data !== 'object') return null;
  const envelope = data as Record<string, unknown>;
  const candidate = envelope.result ?? envelope;
  if (candidate == null || typeof candidate !== 'object') return null;
  const row = candidate as Record<string, unknown>;
  if (typeof row.found !== 'boolean') return null;
  if (typeof row.alreadyInMasterChain !== 'boolean') return null;
  return candidate as U3141ResolveLotDetailForGraphAddResult;
}

/** Entrées concordance cochées par défaut (preview ou lotDetail). */
export function u3141DefaultSelectedConcordanceEntries(
  resolve: U3141ResolveLotDetailForGraphAddResult,
): string[] {
  const fromPreview = resolve.concordancePreview?.map((row) => row.entry).filter(Boolean) ?? [];
  if (fromPreview.length > 0) return fromPreview;
  return [...(resolve.lotDetail?.concordance ?? [])];
}
